import React from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiHome, FiSun, FiShoppingCart, FiTrendingUp, FiClock, FiAward,
  FiDroplet, FiGrid, FiSettings, FiChevronLeft, FiHeart, FiTarget,
  FiAlertTriangle, FiCalendar, FiBarChart, FiColumns, FiShuffle,
  FiShoppingBag, FiCheckSquare, FiBox, FiFeather, FiShield
} = FiIcons;

const NAV_GROUPS = [
  {
    title: 'MindGarden',
    items: [
      { path: '/', icon: FiHome, label: 'Today' },
      { path: '/plan', icon: FiCheckSquare, label: 'Plan' },
      { path: '/studio', icon: FiClock, label: 'Studio' },
      { path: '/garden', icon: FiTarget, label: 'Garden' },
      { path: '/journal', icon: FiHeart, label: 'Journal' },
    ]
  },
  {
    title: 'Account',
    items: [
      { path: '/membership', icon: FiShoppingBag, label: 'Membership' },
      { path: '/settings', icon: FiSettings, label: 'Settings' },
    ]
  }
];

export default function Sidebar({ collapsed, mobileOpen, onToggleCollapse, onCloseMobile }) {
  return (
    <>
      {/* Desktop Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: collapsed ? 72 : 240 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:z-30 bg-white/95 backdrop-blur-sm border-r border-sage/20 shadow-lg"
      >
        <SidebarContent collapsed={collapsed} onToggleCollapse={onToggleCollapse} showCollapseButton={true} />
      </motion.aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.aside
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white/95 backdrop-blur-sm border-r border-sage/20 shadow-2xl"
          >
            <SidebarContent onCloseMobile={onCloseMobile} />
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}

function SidebarContent({ collapsed = false, onToggleCollapse, onCloseMobile, showCollapseButton = false, }) {
  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center p-4 border-b border-sage/20 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden bg-white">
            <img src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758314084196-MG-%2B-Wordmark-Logo.jpg" alt="MindGarden Logo" className="w-full h-full object-contain" />
          </div>
          <AnimatePresence mode="wait">
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
              >
                <h1 className="text-lg font-cormorant font-bold text-charcoal">
                  MindGarden
                </h1>
                <p className="text-[10px] font-dm-sans text-charcoal/60 whitespace-nowrap">
                  Your effort, made visible.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto pb-20 custom-scrollbar px-3 py-4 space-y-6">
        {NAV_GROUPS.map((group) => (
          <div key={group.title} className="space-y-1">
            {!collapsed && (
              <h3 className="px-3 text-[10px] font-bold uppercase tracking-widest text-charcoal/40 mb-2">
                {group.title}
              </h3>
            )}
            <ul className="space-y-1">
              {group.items.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={onCloseMobile}
                    className={({ isActive }) =>
                      `flex items-center p-3 rounded-xl transition-all duration-200 touch-manipulation group ${isActive
                        ? 'bg-sage/20 text-charcoal border border-sage/30'
                        : 'text-charcoal/70 hover:bg-sage/10 hover:text-charcoal'
                      }`
                    }
                  >
                    <SafeIcon icon={item.icon} className="w-5 h-5 flex-shrink-0" />
                    <AnimatePresence mode="wait">
                      {!collapsed && (
                        <motion.span
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          transition={{ duration: 0.2 }}
                          className="ml-3 font-dm-sans font-medium truncate"
                        >
                          {item.label}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Collapse Toggle */}
      {showCollapseButton && (
        <div className="p-4 border-t border-sage/20 flex-shrink-0">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="w-full flex items-center justify-center p-3 rounded-xl bg-sage/10 hover:bg-sage/20 transition-colors duration-200 touch-manipulation"
          >
            <motion.div
              animate={{ rotate: collapsed ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <SafeIcon icon={FiChevronLeft} className="w-5 h-5 text-charcoal" />
            </motion.div>
          </motion.button>
        </div>
      )}
    </div>
  );
}